import { useApplicationTriggerFlow } from '@/components/Functional/hooks-core/useApplicationTriggerFlowStateMachine'
import { useCampaignSlug } from '@/components/Functional/hooks-core/useCampaignSlug'
import { AppMachineContext } from '@/components/Functional/hooks-core/useRaundLifeCycle'
import { DialogNearbySpots } from '@/components/Layout/DialogNearbySpots'
import { ScanCodeOverlay } from '@/components/Layout/scanner/components/ScanCodeOverlay'
import { BottomNavigation as BottomNavigationUI } from '@/components/UI/BottomNavigation'
import type { BottomNavigationProps as UIProps } from '@/components/UI/BottomNavigation'
import { extractQrHash, parseSpotIdFromHash } from '@/lib/qrUtils'
import { useSelector } from '@xstate/react'
import type { Result } from '@zxing/library'
import { useRouter } from 'next/router'
import { memo, useCallback, useState } from 'react'

export type BottomNavigationProps = Pick<UIProps, 'activeItem' | 'onItemClick' | 'showStamp'>

export const BottomNavigation = memo(function BottomNavigation(props: BottomNavigationProps) {
  const trigger = useApplicationTriggerFlow()
  const router = useRouter()
  const { gen } = useCampaignSlug()

  // キャンペーンの stampTypes を AppMachineContext から取得
  const appActorRef = AppMachineContext.useActorRef()
  const stampTypes = useSelector(appActorRef, (s) => s.context.campaignData?.stampTypes ?? [])

  // スタンプタイプが QR_CODE のみか判定（空配列は除外）
  const isAllQrCampaign = stampTypes.length > 0 && stampTypes.every((t) => t === 'QR_CODE')

  // QR スキャンオーバーレイの開閉状態
  const [isQrScanOpen, setIsQrScanOpen] = useState(false)

  const handleSelectSpot = useCallback(
    (spotId: number) => {
      trigger.selectSpot(spotId)
      router.push({ pathname: gen('/spots/detail'), query: { id: spotId } })
    },
    [trigger, router, gen],
  )

  // GET ボタン押下ハンドラ
  const handleApplicationTriggerClick = useCallback(() => {
    if (isAllQrCampaign) {
      // QR のみキャンペーン → GPS 取得をスキップして直接スキャナーを開く
      setIsQrScanOpen(true)
    } else {
      // 通常フロー → GPS 取得 + 近くのスポットダイアログ
      trigger.trigger()
    }
  }, [isAllQrCampaign, trigger])

  // QR 読み取り成功ハンドラ
  const handleQrRead = useCallback(
    (result: Result): boolean => {
      const text = result.getText()
      const hash = extractQrHash(text)
      if (!hash) return false // 無効な QR → スキャン継続

      const spotId = parseSpotIdFromHash(hash)
      if (!spotId) return false // スポット ID 取得不可 → スキャン継続

      // ScanCodeOverlay は opened 時に history.pushState({ scanCodeOverlay: true }) を積んでいる。
      // onClose() 後の cleanup で history.back() が呼ばれると router.push が打ち消されるため、
      // 先に replaceState でマーカーを除去し cleanup が history.back() を呼ばないようにする。
      if (window.history.state?.scanCodeOverlay) {
        window.history.replaceState(null, '')
      }
      // router.replace を使うことで、オーバーレイ用のエントリを /spots/detail URL で上書きし、
      // 余分な履歴エントリを残さずに遷移できる。
      // また replaceState により cleanup の history.back() 条件が偽になるため、遷移が打ち消されない。
      void router.replace(gen(`/spots/detail?id=${spotId}&fromQr=${encodeURIComponent(hash)}`))
      return true // オーバーレイを閉じる
    },
    [router, gen],
  )

  return (
    <>
      <BottomNavigationUI
        {...props}
        onApplicationTriggerClick={handleApplicationTriggerClick}
        useQrIcon={isAllQrCampaign}
      />

      {/* QR のみキャンペーン：直接スキャナーを表示 */}
      {isAllQrCampaign && (
        <ScanCodeOverlay opened={isQrScanOpen} onClose={() => setIsQrScanOpen(false)} onRead={handleQrRead} mode="qr" />
      )}

      {/* 通常キャンペーン：近くのスポットダイアログ */}
      {!isAllQrCampaign && (
        <DialogNearbySpots
          opened={trigger.isSelectingSpot}
          onClose={trigger.closeDialog}
          nearbySpots={trigger.nearbySpots}
          isAllQrCampaign={false}
          onSelect={handleSelectSpot}
          gpsAvailable={trigger.gpsAvailable}
        />
      )}
    </>
  )
})
